import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { formatFuelQuantities } from '../../utils/formatFuelQuantities';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATUS_COLORS = {
  pending: 'warning',
  accepted: 'info',
  active: 'primary',
  complete: 'success',
  cancelled: 'error',
};

function formatMonthYear(year, month) {
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

function formatDisplayDate(isoDate) {
  const [y, m, d] = isoDate.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function shiftMonth(year, month, delta) {
  let m = month + delta;
  let y = year;
  while (m < 1) {
    m += 12;
    y -= 1;
  }
  while (m > 12) {
    m -= 12;
    y += 1;
  }
  return { year: y, month: m };
}

function buildCalendarCells(year, month) {
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];

  for (let i = 0; i < firstDay; i += 1) {
    cells.push({ key: `pad-start-${i}`, day: null });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    cells.push({ key: iso, day, iso });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ key: `pad-end-${cells.length}`, day: null });
  }
  return cells;
}

export default function ScheduleCalendarSection({
  scheduleUrl,
  resourceName = '',
  subtitle = '',
  entityLabel = 'resource',
  embedded = false,
  showDriver = false,
  showVehicle = false,
  enabled = true,
}) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  );

  useEffect(() => {
    if (!enabled || !scheduleUrl) return undefined;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(scheduleUrl, { params: { year, month } });
        if (!cancelled) setSchedule(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.error ||
              err.response?.data?.detail ||
              'Failed to load schedule.'
          );
          setSchedule(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scheduleUrl, year, month, enabled]);

  const eventsByDate = useMemo(() => {
    const map = {};
    (schedule?.events || []).forEach((event) => {
      if (!map[event.display_date]) map[event.display_date] = [];
      map[event.display_date].push(event);
    });
    return map;
  }, [schedule]);

  const calendarCells = useMemo(() => buildCalendarCells(year, month), [year, month]);
  const selectedEvents = eventsByDate[selectedDate] || [];

  const goToPrevMonth = () => {
    const next = shiftMonth(year, month, -1);
    setYear(next.year);
    setMonth(next.month);
    setSelectedDate(`${next.year}-${String(next.month).padStart(2, '0')}-01`);
  };

  const goToNextMonth = () => {
    const next = shiftMonth(year, month, 1);
    setYear(next.year);
    setMonth(next.month);
    setSelectedDate(`${next.year}-${String(next.month).padStart(2, '0')}-01`);
  };

  const goToToday = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const d = now.getDate();
    setYear(y);
    setMonth(m);
    setSelectedDate(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  };

  const description =
    subtitle ||
    (resourceName
      ? `Jobs scheduled or completed for ${resourceName}.`
      : `Jobs scheduled or completed for this ${entityLabel}.`);

  return (
    <Paper sx={{ p: embedded ? 2 : 3, mb: embedded ? 0 : 3 }} variant={embedded ? 'outlined' : 'elevation'}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant={embedded ? 'h6' : 'h5'} component="h2" sx={{ fontWeight: 700 }}>
            Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton aria-label="Previous month" onClick={goToPrevMonth} size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ minWidth: 160, textAlign: 'center', fontWeight: 600 }}>
            {formatMonthYear(year, month)}
          </Typography>
          <IconButton aria-label="Next month" onClick={goToNextMonth} size="small">
            <ChevronRightIcon />
          </IconButton>
          <Chip label="Today" size="small" onClick={goToToday} sx={{ ml: 1, cursor: 'pointer' }} />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: 0.5,
              mb: 2,
            }}
          >
            {WEEKDAY_LABELS.map((label) => (
              <Typography
                key={label}
                variant="caption"
                color="text.secondary"
                align="center"
                sx={{ fontWeight: 600, py: 0.5 }}
              >
                {label}
              </Typography>
            ))}

            {calendarCells.map((cell) => {
              if (!cell.day) {
                return <Box key={cell.key} sx={{ minHeight: 44 }} />;
              }

              const dayEvents = eventsByDate[cell.iso] || [];
              const isSelected = selectedDate === cell.iso;
              const isToday =
                cell.iso ===
                `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

              return (
                <Box
                  key={cell.key}
                  onClick={() => setSelectedDate(cell.iso)}
                  sx={{
                    minHeight: 44,
                    p: 0.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'action.selected' : isToday ? 'action.hover' : 'background.paper',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: isToday ? 700 : 500 }}>
                    {cell.day}
                  </Typography>
                  {dayEvents.length > 0 && (
                    <Box
                      sx={{
                        mt: 0.25,
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {formatDisplayDate(selectedDate)}
          </Typography>

          {selectedEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No jobs on this day.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {selectedEvents.map((event) => (
                <Paper key={event.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {event.customer_name || 'Unknown customer'}
                    </Typography>
                    <Chip
                      label={event.job_status}
                      size="small"
                      color={STATUS_COLORS[event.job_status] || 'default'}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {event.job_location || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Fuel:</strong>{' '}
                    {formatFuelQuantities({
                      diesel_quantity: event.diesel_quantity,
                      def_quantity: event.def_quantity,
                      dyed_quantity: event.dyed_quantity,
                    })}
                  </Typography>
                  {showDriver && event.driver_name && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Driver:</strong> {event.driver_name}
                    </Typography>
                  )}
                  {showVehicle && event.vehicle_info && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Vehicle:</strong> {event.vehicle_info}
                    </Typography>
                  )}
                  {event.frequency && event.frequency !== 'one-time' && (
                    <Typography variant="caption" color="text.secondary">
                      Frequency: {event.frequency}
                    </Typography>
                  )}
                  {event.date_source === 'scheduled' && event.scheduled_date && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Scheduled: {new Date(event.scheduled_date).toLocaleString()}
                    </Typography>
                  )}
                  {event.completed_at && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Completed: {new Date(event.completed_at).toLocaleString()}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}

          {!loading && schedule?.events?.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No jobs for this {entityLabel} in {formatMonthYear(year, month)}.
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
}
